import { I18n } from "react-redux-i18n";
import auth, { featureEnabled } from "../auth";
import { getPaymentMethod } from "../tableMatePaid";
import moment from "moment";

const TAB = "\t";

const OUTPUT_ELECTRONIC = "0";
const OUTPUT_PAPER = "1";
const EXTERNAL_POWER_OK = "0";
const DEFAULT_CLERK_ID = "1";
const DEFAULT_CLERK_PASSWORD = "0";

const toBase64 = (text) => {
    try {
        return btoa(unescape(encodeURIComponent(text || "")));
    } catch (error) {
        console.error("b1 command encryption failed", error);
        return "";
    }
};

export const encryptCommandLine = (line) => {
    const match = (line || "").match(/^command=([\s\S]*)$/i);
    if (!match) {
        return line;
    }
    return "command=" + toBase64(match[1]) + "&encrypted=1";
};

export const encryptCommandPayload = (payload) => {
    if (!payload) {
        return payload;
    }

    const lines = payload.split(/\r?\n/);
    const encryptedLines = [];
    let currentCommand = null;

    lines.forEach((line) => {
        if (/^command=/i.test(line)) {
            if (currentCommand) {
                encryptedLines.push(encryptCommandLine(currentCommand));
            }
            currentCommand = line;
            return;
        }

        if (currentCommand) {
            currentCommand += line === "" ? "\n" : "\n" + line;
            return;
        }

        encryptedLines.push(line);
    });

    if (currentCommand) {
        encryptedLines.push(encryptCommandLine(currentCommand));
    }

    return encryptedLines.join("\r\n") + (payload.endsWith("\r\n") || payload.endsWith("\n") ? "\r\n" : "");
};

const buildOpenFiscalReceiptCommand = (invoiceType = "") => {
    return "command=ofr" + TAB + [
        DEFAULT_CLERK_ID,
        DEFAULT_CLERK_PASSWORD,
        OUTPUT_PAPER,
        EXTERNAL_POWER_OK,
        invoiceType
    ].join(",") + "\r\n";

};

//sbd\t0,00,\n\n                  \t1+1500*+1
//sbd\t0,00,\n\nPipisztikus+S+adag\t1+2790*+1

const buildSaleByTaxCommand = (name, taxId, amount, quantity) => {
    const normalizedName = ((name || "").trim());
    const normalizedAmount = String(amount).replace(",", ".");
    const normalizedQuantity = String(quantity || 1).replace(",", ".");
    return "command=sbd" + TAB
        + "0,00,"
        + "\n"
        + "\n"
        + normalizedName + TAB
        + quantity + "+" + normalizedAmount + "*+1"
        + "\r\n";
};

const buildCashMovementCommand = (command, reason, paidMode, amount) => {
    const normalizedAmount = String(amount).replace(",", ".");
    return "command=" + command + TAB
        + reason + TAB
        + TAB
        + TAB
        + OUTPUT_PAPER + TAB
        + TAB
        + "\n"
        + paidMode + "," + normalizedAmount
        + "\r\n";
};

const buildCustomerInfo = (data = {}) => {
    const customerTaxNum = (data.customerTaxNum || "").trim();
    const customerName = (data.customerName || "").trim();
    const customerPostCode = (data.customerPostCode || "").trim();
    const customerTown = (data.customerTown || "").trim();
    const customerAddress = (data.customerAddress || "").trim();
    const vatStatus = customerTaxNum ? "0" : "1";
    const idType = customerTaxNum ? "0" : "";

    return [
        "0," + vatStatus,
        idType,
        customerTaxNum,
        "",
        customerName,
        "",
        "HU",
        "",
        customerPostCode,
        customerTown,
        customerAddress,
        "",
        "",
        ""
    ].join(TAB);
};

const buildStornoCommand = (data, paymentCode = "P") => {
    const receiptNo = Number(data.receiptNumber.substring(5));
    const zCount = Number(data.receiptNumber.substring(0, 4));
    const stornoReason = data.reason || "0";
    const url = "command=sfr" + TAB + [
        "10",
        "XXXXXXXXX",
        "",
        moment().format("YYMMDD"),
        "F",
        zCount,
        receiptNo,
        stornoReason,
        buildCustomerInfo(data),
        paymentCode
    ].join(TAB);
    console.log(url);
    return url;
};

// Returning goods (e.g. bottles) and paying the money back is only possible on the b1 device with a storno
// that refers to a receipt which does not exist. These numbers must never match a real receipt (Z count 9999, receipt 99999).
const FALSE_STORNO_Z_COUNT = 1;
const FALSE_STORNO_RECEIPT_NO = 1;

const VAT_CODES = {
    5: "A",
    18: "B",
    27: "C",
    s5: "A",
    s18: "B",
    s27: "C",
    undefined: "E",
    null: "E",
    0: "E",
};

const PAYMENT_CODES = {
    "cash": "P",
    "card": "N",
    "hellopay": "N",
    "online": "N",
    "teya": "N",
    "bizpay": "N",
    "paynance": "N",
    "utalvany": "O3"
};

// True when there is at least one non-zero line and every non-zero line is negative.
export const hasOnlyNegativeAmounts = (amounts) => {
    const lines = Object.values(amounts || {}).filter(category => category.amount || category.service_amount);
    return lines.length > 0 && lines.every(category => !(category.amount > 0) && !(category.service_amount > 0));
};

const buildFalseStornoReceipt = (amounts, types) => {
    const payments = (types || []).filter(t => Number(t.amount) < 0);
    if (payments.length === 0) {
        return "";
    }

    const ap = "Y12345678";
    const toPositive = (value) => String(Math.abs(Number(value))).replace(",", ".");

    // bbr: OutputType&ReadDataFlag, ApNumber, taxNumber, Date, Type[InvoiceType[RecycleReceipt]] (F01 = fiscal, recycle/bottle return),
    // Z1Count, ReceiptNo, Reason (1 = faulty goods), CustomerInfo. It refers to a receipt that does not exist.
    let url = "command=bbr" + TAB + [
        "11",
        ap,
        "000000",
        moment().format("YYMMDD"),
        "F01",
        FALSE_STORNO_Z_COUNT,
        FALSE_STORNO_RECEIPT_NO,
        "1",
        ["1,1"].concat(new Array(13).fill("")).join(TAB)
    ].join(TAB) + "\r\n";

    const buildItem = (name, vatCode, price, quantity) => {
        const itemName = (name || "").trim().replace(/[\t\r\n]+/g, " ");
        return "command=sbd" + TAB + "0,0,db\n\n" + itemName + TAB + vatCode + "+" + toPositive(price) + "*+" + quantity + "\r\n";
    };

    const services = {};
    Object.values(amounts).sort((a, b) => a.entryOrder - b.entryOrder).filter(category => category.amount || category.service_amount).forEach(category => {
        const count = Math.abs(Number(category.count)) || 1;
        if (category.amount) {
            url += buildItem(category.name, VAT_CODES[category.vat] || "E", category.amount, count);
        }
        if (category.service_amount) {
            services[category.vat] = (services[category.vat] || 0) + Math.abs(category.service_amount) * count;
        }
    });

    Object.keys(services).forEach(key => {
        if (VAT_CODES["s" + key]) {
            const name = services[key] > 2 ? I18n.t("admin_local.fiscat_service_fee") + " (" + I18n.t("admin_local.vat") + " " + key + "%)" : I18n.t("admin_local.rounding");
            url += buildItem(name, VAT_CODES["s" + key], services[key], 1);
        }
    });

    // cfr for a free storno/return: RefundParam (1 = recycle payment), then PayCode,PayAmt pairs.
    const paidParams = payments.map(t => {
        const paymentMethod = getPaymentMethod(t.type);
        const paymentType = paymentMethod?.method?.type ?? paymentMethod?.type ?? t.type;
        const normalizedPaymentType = typeof paymentType === "string" ? paymentType.toLowerCase() : paymentType;
        if (t.currency === "EUR" && normalizedPaymentType === "cash") {
            return "F0," + Math.abs(Math.round(100 * t.amount) / 100);
        }
        return (PAYMENT_CODES[normalizedPaymentType] || "N") + "," + toPositive(t.amount);
    });

    url += "command=cfr" + TAB + ["1"].concat(paidParams).join(TAB) + "\r\n";
    return url;
};

export const generate_b1 = (def, amounts, cashierType, payable, types, amountPaidExchangeRate, serviceVat, tip, type = "cash", tableMate, seqnr) => {
    if (hasOnlyNegativeAmounts(amounts)) {
        const stornoUrl = buildFalseStornoReceipt(amounts, types);
        console.log(stornoUrl);
        def.resolve(encryptCommandPayload(stornoUrl));
        return;
    }

    var url = "";
    var b1 = {
        5: "A",
        18: "B",
        27: "C",
        s5: "A",
        s18: "B",
        s27: "C",
        undefined: "E",
        null: "E",
        0: "E",
    };

    var services = {};
    var count = 1;

    var totalAmount = 0;

    url = "";

    const InvoiceType = "";

    url += buildOpenFiscalReceiptCommand(InvoiceType);

    if (seqnr)
        url += "command=pcd" + TAB + "T" + TAB + 0xFF + "\r" + I18n.t("admin_local.order_sequence_number") + " #" + seqnr + "\r\n";

    Object.values(amounts).sort((a, b) => a.entryOrder - b.entryOrder).filter(category => category.amount + category.service_amount).forEach(category => {
        if (category.amount) {
            //eslint-disable-next-line no-useless-concat
            url += buildSaleByTaxCommand(category.name, b1[category.vat], category.amount, category.count);
        }

        totalAmount += category.amount;
        count++;
        if (category.service_amount > 0) {
            if (!services[category.vat]) {
                services[category.vat] = 0;
            }
            services[category.vat] += category.service_amount * category.count;
            totalAmount += category.service_amount * category.count;
        }
    });

    /*
    if (Object.keys(services).length && Math.abs(payable - totalAmount) < 5) {
        services[Object.keys(services)[0]] -= totalAmount - payable;
    }*/

    var serviceUrl = "";

    Object.keys(services).forEach(key => {
        if (b1["s" + key])
            url += buildSaleByTaxCommand(
                services[key] > 2 ? I18n.t("admin_local.fiscat_service_fee") + " (" + I18n.t("admin_local.vat") + " " + key + "%)" : I18n.t("admin_local.rounding"),
                b1["s" + key],
                auth.round(services[key]),
                1
            );
        count++;
    });

    //url += "command=pcd" + TAB + "C" + TAB + "1,690123456789" + "\r\n";
    if (featureEnabled("history/feedback") && tableMate?.id) {
        url += "command=pcd" + TAB + "T" + TAB + 0xFF + "\r" + I18n.t("admin_local.scanit") + "\r\n";
        url += "command=pcd" + TAB + "T" + TAB + 0xFF + "\r" + I18n.t("admin_local.rateus") + "\r\n";
        // The payload is base64 encrypted, the device does not URL-decode it afterwards, so the QR data must be sent as plain text.
        url += "command=pcd" + TAB + "C" + TAB + "q,https://restaurant.eatwithme.online" + (auth.myStatus.test_mode ? ":8444" : "") + "/feedback.html?r=" + auth.myStatus.restaurant_global_id + "&t=" + sessionStorage.tableNumberSelected + "&m=" + tableMate.id + "&q=" + tableMate.qrToken + "&p=TABLEMATEPAYMENTID" + "\r\n";
    } else {
        const isShopMode = auth?.myStatus?.restaurant_settings?.["shop-mode"];
        if (isShopMode) {
            url += "command=pcd" + TAB + "C" + TAB + "2,TABLEMATEPAYMENTID0" + "\r\n";
        } else {
            const scanABill = featureEnabled("history/scan-a-bill");
            if (scanABill && tableMate) {
                console.log("https://restaurant.eatwithme.online" + (auth.myStatus.test_mode ? ":8444" : "") + "/map.html?r=" + auth.myStatus.restaurant_global_id + "&t=" + sessionStorage.tableNumberSelected + "&m=" + tableMate.id + "&q=" + tableMate.qrToken + "&p=true&c=true");
                url += "command=pcd" + TAB + "C" + TAB + "q,https://restaurant.eatwithme.online" + (auth.myStatus.test_mode ? ":8444" : "") + "/map.html?r=" + auth.myStatus.restaurant_global_id + "&t=" + sessionStorage.tableNumberSelected + "&m=" + tableMate.id + "&q=" + tableMate.qrToken + "&p=true&c=true&a=m" + "\r\n";
            }
        }
    }

    //url += "command=pcd" + TAB + "C" + TAB + "1%2C" + "TABLEMATEPAYMENTID" + "\r\n";

    var paymentMethods = {
        "cash": "P",
        "card": "N",
        "hellopay": "N",
        "online": "N",
        "teya": "N",
        "bizpay": "N",
        "paynance": "N",
        "utalvany": "O3"
    }

    types.filter(t => Number(t.amount) > 0).forEach((t, ind) => {
        //if (ind > 0)
        //  url += "|";
        const paymentMethod = getPaymentMethod(t.type);
        const paymentType = paymentMethod?.method?.type ?? paymentMethod?.type ?? t.type;
        const normalizedPaymentType = typeof paymentType === "string" ? paymentType.toLowerCase() : paymentType;
        const paymentCode = paymentMethods[normalizedPaymentType] || "N";

        var roundFunction = auth.myStatus.restaurant_base_currency["javaScriptRoundFunction"];
        var amount = t.amount;
        // eslint-disable-next-line no-eval
        //amount = eval(roundFunction.replace("amount", t.amount));

        if (t.currency === "EUR" && normalizedPaymentType === "cash")
            url += "command=pmt" + TAB + TAB + TAB + "F0" + TAB + Math.round(100 * t.amount) / 100 + "\r\n";
        else {
            url += "command=pmt" + TAB + TAB + TAB + paymentCode + TAB + amount + "\r\n";
        }
    });


    url += "command=cfr" + TAB + "\r\n";
    /*
    url += "command=" + "onfr\r\n";
    url += "command=" + "pnft" + TAB + 0x80 + "\r" + "jhkjhkjhkh" + "\n";
    url += "command=" + "cnfr\r\n";
    */

    if (types.filter(t => t.amount > 0).length === 0) {
        url = "";
    }

    if (tip > 0) {
        if (type === "cash")
            url += buildCashMovementCommand("ra", "7", "P", Math.floor(tip / 5) * 5);
        else
            url += buildCashMovementCommand("ra", "7", "N", tip);
    }



    console.log(url);
    //def.resolve("encrypted=1&command=c2JkCTAsMDAsCgoJMSsxNTAwKisx");
    const u = encryptCommandPayload(url);
    def.resolve(u);
}


export const dayOpen_b1 = (amount = 0) => {
    return { text: "command=fod" + TAB + "R\r\ncommand=fod" + TAB + "P," + amount + "\r\n", pattern: ".*" };
}

// Read status flags from the Status field.
// Byte2 bit6 set means the day is open.
// Byte0 bit6 together with Byte2 bit6 means the day was left open too long and must be closed/opened again.
// Byte1 bit5 set means the printer cover is open.
// Byte2 bit0 set means the printer is out of paper.
// Status format is Byte0-Byte1-Byte2-Byte3-Byte4-Byte5 in hex (e.g. 80-80-80-80-80-94).
export const parseCashRegisterStatus_b1 = (status) => {
    if (typeof status !== "string") {
        return { open: false, needsCloseOpen: false, coverOpen: false, outOfPaper: false };
    }

    const bytes = status.split("-");
    if (bytes.length < 6) {
        return { open: false, needsCloseOpen: false, coverOpen: false, outOfPaper: false };
    }

    const byte0 = parseInt(bytes[0], 16);
    const byte1 = parseInt(bytes[1], 16);
    const byte2 = parseInt(bytes[2], 16);
    if (Number.isNaN(byte0) || Number.isNaN(byte1) || Number.isNaN(byte2)) {
        return { open: false, needsCloseOpen: false, coverOpen: false, outOfPaper: false };
    }

    const byte0Bit6 = (byte0 & 0x40) !== 0;
    const byte1Bit5 = (byte1 & 0x20) !== 0;
    const byte2Bit6 = (byte2 & 0x40) !== 0;
    const byte2Bit0 = (byte2 & 0x01) !== 0;

    return {
        open: byte2Bit6 && !byte0Bit6,
        needsCloseOpen: byte0Bit6 && byte2Bit6,
        coverOpen: byte1Bit5,
        outOfPaper: byte2Bit0,
        byte0Bit6,
        byte1Bit5,
        byte2Bit6,
        byte2Bit0
    };
}

export const isCashRegisterOpen_b1 = () => {
    return {
        text: encryptCommandPayload(`command=rs${TAB}\r\n`),
        pattern: ".*<Status>[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}</Status>.*"
    };
}

export const dayClose_b1 = () => {
    return { text: "command=pgr" + TAB + "1" + TAB + "19\r\n", pattern: ".*" };
    //return "command=pfdrd" + TAB + "220925" + TAB + "280925\r\n";
}

// Prints the electronic journal text (daily closure reports) for a period, dates are YYMMDDhhmm.
export const printDayCloseReports_b1 = (fromDate, toDate) => {
    return { text: encryptCommandPayload("command=pejd" + TAB + "P,1,2,524288," + moment(fromDate).format("YYMMDD") + "0000" + moment(toDate).format("YYMMDD") + "2359" + "\r\n"), pattern: ".*" };
}

// Prints a copy of a stored sale receipt from the electronic journal. receiptNo format: ZZZZ/NNNNN.
// pejd P,1,5,<receipt type>,<Z 4 digits><receipt 5 digits>; type 1 = sale receipt (format taken from a device log).
export const reprintReceipt_b1 = (receiptNo) => {
    const match = /^\s*(\d{1,4})\/(\d{1,5})\s*$/.exec(String(receiptNo || ""));
    if (!match) {
        return null;
    }
    return { text: encryptCommandPayload("command=pejd" + TAB + "P,1,5,1," + match[1].padStart(4, "0") + match[2].padStart(5, "0") + "\r\n"), pattern: ".*" };
}

export const storno_b1 = (data, def) => {
    def.resolve(encryptCommandPayload(buildStornoCommand(data) + "\r\n"));
}

export const refund_b1 = (data, def, amounts, cashierType, payable, types, amountPaidExchangeRate, serviceVat, tip, type = "cash", tableMate, seqnr) => {
    var url = "";
    var b1 = {
        5: "A",
        18: "B",
        27: "C",
        s5: "A",
        s18: "B",
        s27: "C",
        undefined: "E",
        null: "E",
        0: "E",
    };

    var id = 0;

    const prefix = "";
    var services = {};
    var count = 1;

    var totalAmount = 0;

    url = "";

    const ClerkID = "1", Password = "0", ExternalPower = "0", InvoiceType = "";

    url += "command=sar" + TAB + "XXXXXXXXX" + TAB + moment().format("YYMMDD") + TAB + "F" + TAB + Number(data.receiptNumber.substring(0, 4)) + TAB + Number(data.receiptNumber.substring(5)) + TAB + data.reason + TAB + data.customerTaxNum + TAB + data.customerName + TAB + data.customerPostCode + TAB + data.customerTown + TAB + data.customerAddress + TAB + TAB + TAB;

    var paymentMethods = {
        "cash": "P",
        "card": "N",
        "hellopay": "N",
        "online": "N",
        "teya": "N",
        "bizpay": "N",
        "paynance": "N",
        "utalvany": "O3"
    }

    types.filter(t => Number(t.amount) > 0).forEach((t, ind) => {
        //if (ind > 0)
        //  url += "|";
        const paymentMethod = getPaymentMethod(t.type);
        const paymentType = paymentMethod?.method?.type ?? paymentMethod?.type ?? t.type;
        const normalizedPaymentType = typeof paymentType === "string" ? paymentType.toLowerCase() : paymentType;
        const paymentCode = paymentMethods[normalizedPaymentType] || "N";

        var roundFunction = auth.myStatus.restaurant_base_currency["javaScriptRoundFunction"];
        var amount = t.amount;
        // eslint-disable-next-line no-eval
        //amount = eval(roundFunction.replace("amount", t.amount));

        if (t.currency === "EUR" && normalizedPaymentType === "cash")
            url += "F0" + TAB + Math.round(100 * t.amount) / 100 + "\r\n";
        else {
            url += paymentCode + TAB + amount + "\r\n";
        }
    });


    Object.values(amounts).sort((a, b) => a.entryOrder - b.entryOrder).filter(category => category.amount + category.service_amount).forEach(category => {
        if (category.amount) {
            //eslint-disable-next-line no-useless-concat
            url += "command=sbt" + TAB + TAB + encodeURIComponent(category.name.trim()) + TAB + TAB + TAB + TAB + TAB + b1[category.vat] + TAB + "00" + TAB + (category.amount) + TAB + category.count + TAB + "0" + "\r\n";
        }

        totalAmount += category.amount;
        count++;
        if (category.service_amount > 0) {
            if (!services[category.vat]) {
                services[category.vat] = 0;
            }
            services[category.vat] += category.service_amount * category.count;
            totalAmount += category.service_amount * category.count;
        }

    });


    /*
    if (Object.keys(services).length && Math.abs(payable - totalAmount) < 5) {
        services[Object.keys(services)[0]] -= totalAmount - payable;
    }*/

    var serviceUrl = "";

    Object.keys(services).forEach(key => {
        if (b1["s" + key])
            url += "command=sbt" + TAB + TAB + (services[key] > 2 ? I18n.t("admin_local.fiscat_service_fee") + " (" + I18n.t("admin_local.vat") + " " + key + "%)" : I18n.t("admin_local.rounding")) + TAB + TAB + TAB + TAB + TAB + b1["s" + key] + TAB + "00" + TAB + ("" + services[key]).replace(",", ".") + TAB + 1 + TAB + "0,0" + "\r\n";
        count++;
    });

    url += "command=crr" + TAB + 0 + "\r\n";

    console.log(url);
    def.resolve(encryptCommandPayload(url));
}

export const cashin_b1 = (reason, amount) => {
    return encryptCommandPayload(buildCashMovementCommand("ra", reason, "P", amount));
}
export const cashout_b1 = (reason, amount) => {
    return encryptCommandPayload(buildCashMovementCommand("po", reason, "P", amount));
}

/**
 * Generates a non-fiscal slip for kiosk cash payment on an b1 printer.
 *
 * The slip shows the restaurant name, date/time, order sequence number, a
 * breakdown of the ordered items, the total amount payable, and a message
 * directing the guest to pay at the cashier.
 *
 * Commands use the b1 non-fiscal protocol:
 *   onfr  – open non-fiscal receipt
 *   pnft  – print non-fiscal text: pnft<TAB><format><CR><text>
 *             format 0x00 = normal text, 0x80 = centred / double-width header
 *   cnfr  – close non-fiscal receipt
 *
 * @param {jQuery.Deferred} def        Deferred resolved with the command string.
 * @param {number|string}   seqnr      Order sequence number shown on the slip.
 * @param {Object}          amounts    Category-keyed object identical to the one
 *                                     used by generate_b1 (may be null/undefined
 *                                     to omit line items).
 * @param {number}          payable    Total amount the guest must pay.
 * @param {string}          currencyName  Currency symbol/name (e.g. "HUF", "€").
 */
export const generate_nonfiscal_slip_b1 = (def, seqnr, amounts, payable, currencyName, targetTableName) => {
    const SEP = "--------------------------------";
    const FMT_NORMAL = "\x80";   // normal left-aligned text
    const FMT_CENTER = "\x9A";   // centred / double-width header

    const pnft = (fmt, text) =>
        "command=pnft" + TAB + fmt + "\r" + text + "\r\n";

    let url = "";

    url += "command=onfr\r\n";

    url += pnft(FMT_NORMAL, SEP);

    // Sequence number — the key reference the guest brings to the cashier
    if (seqnr)
        url += pnft(FMT_CENTER, I18n.t("admin_local.order_sequence_number") + ": #" + seqnr);

    url += pnft(FMT_NORMAL, SEP);

    // Line items — one line per item matching fiscal receipt style: name  unitPrice  count
    if (amounts) {
        Object.values(amounts)
            .sort((a, b) => (a.entryOrder || 0) - (b.entryOrder || 0))
            .filter(category => category.amount)
            .forEach(category => {
                const name = (category.name || "").trim().toLowerCase();
                const count = category.count || 1;
                const total = category.amount || 0;
                const unitPrice = Math.round((total / count) * 100) / 100;
                const cur = currencyName || "";
                const priceStr = unitPrice + " " + cur;
                const countStr = "" + count;
                // truncate name to leave room for price and count
                const maxName = 31 - priceStr.length - countStr.length - 2;
                const nameStr = name.length > maxName ? name.substring(0, maxName) : name;
                const padding = Math.max(1, 31 - nameStr.length - priceStr.length - countStr.length);
                url += pnft(FMT_NORMAL, nameStr + " ".repeat(padding) + priceStr + " " + countStr);
            });

        url += pnft(FMT_NORMAL, SEP);
    }

    // Total
    url += pnft(FMT_CENTER,
        I18n.t("admin_local.total") + ": " + payable + " " + (currencyName || ""));

    url += pnft(FMT_NORMAL, SEP);

    // Order not yet finalized
    url += pnft(FMT_CENTER, I18n.t("admin_local.kiosk_order_not_finalized"));

    // Go to table to pay
    if (targetTableName)
        url += pnft(FMT_CENTER, String.format(I18n.t("admin_local.kiosk_go_to_table_to_pay"), targetTableName));

    // Feed paper so the slip can be torn off cleanly
    url += pnft(FMT_NORMAL, "");
    url += pnft(FMT_NORMAL, "");
    url += pnft(FMT_NORMAL, "");
    url += pnft(FMT_NORMAL, "");

    url += "command=cnfr\r\n";

    console.log(url);
    def.resolve(encryptCommandPayload(url));
};



/*
//url += "command=sar" + TAB + 1 + TAB + moment().format("YYMMDD") + TAB + "F" + TAB + 0 + TAB + TAB + TAB + 1 + TAB + 1 + TAB + TAB + TAB + TAB + TAB + TAB + "P\r\n";
url += "command=sar" + TAB + 0 + TAB + moment().format("YYMMDD") + TAB + "F" + TAB + 1 + TAB + 1 + TAB + 0 + TAB + TAB + TAB + TAB + TAB + TAB + TAB + TAB + "\r\n";
//url += "command=sbt" + TAB + TAB + TAB + TAB + TAB + TAB + TAB + TAB + 50 + TAB + 1 + TAB + TAB + "0," + "\r\n";

url += "command=sbt" + TAB + TAB + "ccc" + TAB + TAB + TAB + TAB + TAB + b1["null"] + TAB + "00" + TAB + 50 + TAB + 1 + TAB + "0" + "\r\n";

url += "command=crr" + TAB + 1 + TAB + "06" + "\r\n";
*/
